import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Menu, X, ChevronRight, LogOut, LayoutDashboard } from 'lucide-react'
import { ZYPHERON_LOGOS } from '../../assets/logos'
import { useAuth } from '../../contexts/AuthContext'
import { trackEvent } from '../../utils/analytics'

interface NavItem {
  label: string;
  path: string;
  onClick?: () => void;
}

const TopNavBar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const navItems: NavItem[] = [
    { label: 'DOWNLOAD', path: '/download' },
    { label: 'CLI', path: '/cli' },
    { label: 'CLOUD', path: '/cloud' },
    { label: 'ARK C2', path: '/ark-c2' },
    {
      label: 'PRICING',
      path: '/#pricing',
      onClick: () => {
        if (location.pathname === '/') {
          document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' })
        } else {
          navigate('/')
          setTimeout(() => document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' }), 100)
        }
      }
    },
    { label: 'BLOG', path: '/blog' },
    { label: 'DOCS', path: '/documentation' }
  ]

  const handleStartTrial = (surface: string) => {
    trackEvent('trial_cta_click', { surface })
    navigate(location.pathname === '/cloud' || location.pathname.startsWith('/billing/cloud') ? '/billing/cloud' : '/billing/ide')
  }
  // Generic nav sign-in stays local and doesn't force a detour into the
  // Cloud/Dashboard app — that's only for someone explicitly choosing it
  // (e.g. from the Cloud marketing page), not the default "sign in" action.
  const handleLogin = () => navigate('/login')
  const handleSubscribe = () => navigate('/subscribe')
  const handleSignOut = async () => {
    setSigningOut(true)
    await signOut()
    setSigningOut(false)
    setMobileMenuOpen(false)
    navigate('/')
  }

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true
    if (path !== '/' && location.pathname.startsWith(path)) return true
    return false
  }

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/[0.08] bg-void/90 backdrop-blur-xl">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 xl:px-8">
        <div className="flex h-16 items-center justify-between gap-6">
          {/* Left: Brand */}
          <div className="flex min-w-0 items-center">
            <button
              type="button"
              className="flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zypheron-400 focus-visible:ring-offset-2 focus-visible:ring-offset-void rounded-[2px]"
              onClick={() => navigate('/')}
              aria-label="Go to Zypheron home"
            >
              <img src={ZYPHERON_LOGOS.icon} alt="" className="h-9 w-9 object-contain transition-opacity group-hover:opacity-80" />
              <div className="text-lg font-bold tracking-tight text-white transition-colors font-display group-hover:text-zypheron-500">
                ZYPHERON
              </div>
            </button>

            {/* Desktop Nav */}
            <nav aria-label="Primary navigation" className="ml-8 hidden items-center gap-1 xl:flex">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => item.onClick ? item.onClick() : navigate(item.path)}
                  className={`relative px-3 py-5 text-[11px] font-mono-tech tracking-[0.08em] transition-colors ${isActive(item.path)
                    ? 'text-white after:absolute after:inset-x-3 after:bottom-0 after:h-px after:bg-zypheron-400'
                    : 'text-gray-500 hover:text-white'
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Right: Actions */}
          <div className="flex shrink-0 items-center gap-3">
            <div className="hidden items-center gap-3 xl:flex">
              {user ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center gap-2 px-3 py-2 text-[11px] font-mono-tech tracking-[0.08em] text-gray-400 transition-colors hover:text-white"
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" />
                    DASHBOARD
                  </button>
                  <button
                    onClick={() => void handleSignOut()}
                    disabled={signingOut}
                    aria-label={signingOut ? 'Signing out' : 'Sign out'}
                    title={user.email ? `Sign out ${user.email}` : 'Sign out'}
                    className="p-2 text-gray-500 transition-colors hover:text-white disabled:opacity-50"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button onClick={handleLogin} className="px-3 py-2 text-[11px] font-mono-tech tracking-[0.08em] text-gray-400 transition-colors hover:text-white">
                  SIGN IN
                </button>
              )}
            </div>

            <button
              onClick={() => handleStartTrial('nav_desktop')}
              className="hidden items-center gap-2 whitespace-nowrap bg-zypheron-500 px-4 py-2.5 text-[11px] font-bold tracking-[0.06em] text-black transition-colors font-mono-tech hover:bg-zypheron-400 xl:flex"
            >
              <span>START FREE TRIAL</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>

            {/* Mobile Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              className="border border-white/10 p-2 text-gray-400 transition-colors hover:border-white/20 hover:text-white xl:hidden"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div id="mobile-navigation" className="absolute left-0 top-16 w-full border-b border-white/10 bg-void shadow-2xl shadow-black/50 animate-in slide-in-from-top-2 xl:hidden">
          <div className="px-6 py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  item.onClick ? item.onClick() : navigate(item.path)
                  setMobileMenuOpen(false)
                }}
                className={`block w-full text-left py-3 px-4 text-sm font-mono-tech border-l-2 transition-all ${isActive(item.path)
                  ? 'border-zypheron-500 text-white bg-white/5'
                  : 'border-transparent text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
              >
                {item.label}
              </button>
            ))}
            <div className="h-px bg-white/10 my-2" />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={handleSubscribe} className="border border-white/10 py-2.5 text-xs text-gray-400 font-mono-tech hover:text-white">
                EMAIL LIST
              </button>
              <a
                href="https://github.com/KKingZero/Zypheron-CLI"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/10 py-2.5 text-center text-xs text-gray-400 font-mono-tech hover:text-white"
              >
                GITHUB
              </a>
            </div>
              <button onClick={() => handleStartTrial('nav_mobile_menu')} className="w-full py-3 bg-zypheron-500 text-black font-bold font-mono-tech text-center rounded-[2px]">
              START FREE TRIAL
            </button>
            {user ? (
              <button
                onClick={() => void handleSignOut()}
                disabled={signingOut}
                className="w-full py-3 border border-white/10 text-white font-mono-tech text-center rounded-[2px] disabled:opacity-50"
              >
                {signingOut ? 'SIGNING OUT' : `SIGN OUT (${user.email})`}
              </button>
            ) : (
              <button onClick={handleLogin} className="w-full py-3 border border-white/10 text-white font-mono-tech text-center rounded-[2px]">
                SIGN IN
              </button>
            )}
          </div>
        </div>
      )}

      </header>

      {/* Mobile sticky bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-void/95 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-sm xl:hidden">
        <button
          onClick={() => handleStartTrial('mobile_sticky_bar')}
          className="flex w-full items-center justify-center gap-2 bg-zypheron-500 py-3 text-xs font-bold font-mono-tech uppercase tracking-[0.08em] text-black rounded-[2px]"
        >
          Start 14-day trial
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </>
  )
}

export default TopNavBar 
